import type { BadgeTone } from '@/lib/utils/badgeTone'

import type {
  ReservationRefundListItemView,
  ReservationRefundListStatus,
  ReservationRefundMethodFilter,
} from './reservationRefundTypes'
import { validateReservationPeriod, type ReservationPeriod } from './reservationViewModel'

/**
 * 환불 처리 결과 목록 화면 공통 순수 함수(설계 §2) — 필터·커서 조립·상태 톤 매핑.
 * `reservationViewModel.ts`와 같은 규칙 — 상태 색은 프론트가 매핑하되 문구는 항상 서버 `statusLabel`.
 */

export interface ReservationRefundListFilter extends ReservationPeriod {
  /** 빈 배열 = 전체 상태. */
  statuses: ReservationRefundListStatus[]
  /** 빈 배열 = 전체 유형. */
  methods: ReservationRefundMethodFilter[]
  /** `undefined` = 전체(필터 없음). */
  settlementReflected?: boolean
}

export function defaultRefundListFilter(today: string): ReservationRefundListFilter {
  const fromMs = Date.parse(`${today}T00:00:00Z`) - 6 * 86_400_000
  const from = new Date(fromMs).toISOString().slice(0, 10)
  return { from, to: today, statuses: [], methods: [], settlementReflected: undefined }
}

/** #589/#703 공용 62일 상한 — 이 목록도 같은 기간 VO(`VenueReservationPeriod`)를 쓴다. */
export function validateRefundListPeriod(period: ReservationPeriod): string | null {
  return validateReservationPeriod(period)
}

/** 상태 필터 선택지(forward-only) — 실제 라벨은 항상 서버 `statusLabel`을 쓴다. */
export const REFUND_STATUS_OPTIONS: { value: ReservationRefundListStatus; label: string }[] = [
  { value: 'REQUESTED', label: '환불 요청' },
  { value: 'PROCESSING', label: '처리 중' },
  { value: 'COMPLETED', label: '환불 완료' },
  { value: 'FAILED', label: '환불 실패' },
  { value: 'REJECTED', label: '반려' },
]

export const REFUND_METHOD_OPTIONS: { value: ReservationRefundMethodFilter; label: string }[] = [
  { value: 'TOSS_PAYMENTS', label: '토스페이먼츠' },
  { value: 'WALLET', label: '콕 지갑' },
]

/**
 * 🚨 `REJECTED`는 `FAILED`와 다른 톤으로 그린다(api-spec §5, §7 체크리스트) — 운영자가 이미 판단을
 * 끝낸 종국 상태라 "다시 시도할 문제"로 보이는 `danger`가 아니라 `neutral`을 쓴다(재처리 버튼도 없다).
 */
export const REFUND_STATUS_TONE: Record<ReservationRefundListStatus, BadgeTone> = {
  REQUESTED: 'warning',
  PROCESSING: 'warning',
  COMPLETED: 'success',
  FAILED: 'danger',
  REJECTED: 'neutral',
}

/** 목록·CSV 공용 쿼리 파라미터 — `undefined` 필드는 axios가 자동으로 뺀다. */
export function refundListQueryParams(
  filter: ReservationRefundListFilter,
  cursor?: string
): Record<string, string | number | boolean | undefined> {
  return {
    from: filter.from,
    to: filter.to,
    statuses: filter.statuses.length > 0 ? filter.statuses.join(',') : undefined,
    methods: filter.methods.length > 0 ? filter.methods.join(',') : undefined,
    settlementReflected: filter.settlementReflected,
    cursor,
  }
}

/**
 * `settlementCycleMonth=null`을 위한 표시 문구 — api-spec 체크리스트 "반영 실패로 그리지 않는다".
 * `status !== 'COMPLETED'`면 애초에 반영 대상이 아니므로 문구를 가른다.
 */
export function settlementReflectionText(
  item: Pick<ReservationRefundListItemView, 'status' | 'settlementCycleMonth' | 'settlementCarriedOver'>
): string {
  if (item.status !== 'COMPLETED') return '반영 대상 아님'
  if (!item.settlementCycleMonth) return '반영 예정(미종결)'
  return item.settlementCarriedOver ? `${item.settlementCycleMonth} 회차(이월)` : `${item.settlementCycleMonth} 회차`
}

/** 환불 결과 CSV 다운로드 파일명 — api-spec §6 패턴(`refunds_{venueId}_{yyyyMMdd}_{yyyyMMdd}.csv`). */
export function refundsCsvFileName(venueId: string, from: string, to: string): string {
  const compact = (date: string) => date.replaceAll('-', '')
  return `refunds_${venueId}_${compact(from)}_${compact(to)}.csv`
}
